<template>
  <div class="chart-doughnut">
    <div class="container-icon-grand-charles">
      <slot />
    </div>

    <Doughnut :data="data" :plugins="config.plugins" :options="data.options" />
    <p class="info top left">
      <span :class="['label', { small: isSmall }]">{{ topLeft.label }}</span>
      <span :class="['data', { small: isSmall }]">{{ topLeft.data }}{{ topLeft.unit }}</span>
    </p>
    <p class="info top right">
      <span :class="['label', { small: isSmall }]">{{ topRight.label }}</span>
      <span :class="['data', { small: isSmall }]">{{ topRight.data }}{{ topRight.unit }}</span>
    </p>
    <p class="info bottom left">
      <span :class="['data', { small: isSmall }]">{{ bottomLeft.data }}{{ bottomLeft.unit }}</span>
      <span :class="['label', { small: isSmall }]">{{ bottomLeft.label }}</span>
    </p>
    <p class="info bottom right">
      <span :class="['data', { small: isSmall }]">{{ bottomRight.data }}{{ bottomRight.unit }}</span>
      <span :class="['label', { small: isSmall }]">{{ bottomRight.label }}</span>
    </p>
  </div>
</template>

<script lang="ts" setup>
import { Chart as ChartJS, ArcElement } from 'chart.js'
import { Doughnut } from 'vue-chartjs'
import OfficesSvg from '@/assets/images/icons/bureaux.svg'
import RestaurantSvg from '@/assets/images/icons/restaurant.svg'
import OtherSvg from '@/assets/images/icons/autres.svg'
import HVACSvg from '@/assets/images/icons/climatisation.svg'
import ChartDataLabels from 'chartjs-plugin-datalabels'


const props = withDefaults(
  defineProps<{
    topLeft: { label: string; data: number; unit: string }
    topRight: { label: string; data: number; unit: string }
    bottomLeft: { label: string; data: number; unit: string }
    bottomRight: { label: string; data: number; unit: string }
    dataset: {
      backgroundColor: string[]
      data: number[]
    }
    icon?: boolean
    isSmall?: boolean
    datalabels?: boolean
    images?: { urls: string[]; size: number }
    cutout?: number
  }>(),
  {
    icon: false,
    isSmall: false,
    datalabels: false,
    cutout: 120
  }
)

ChartJS.register(ArcElement)

const rawImages = [
  OtherSvg,
  HVACSvg,
  RestaurantSvg,
  OfficesSvg,
]

const images: HTMLImageElement[] = props.images!.urls.map(src => {
  const img = new Image()
  img.src = src
  return img
})

const segmentImage = {
  id: 'imagePlugin',
  afterDraw: (chart: any) => {
    if (!props.images) return
    const { ctx } = chart
    const meta = chart.getDatasetMeta(0)

    meta.data.forEach((arc: any, i: number) => {
      const img = images[i]
      if (!img.complete) return

      const { x, y } = arc.tooltipPosition()
      const size = props.images!.size
      ctx.drawImage(img, x - size / 2, y - size / 2, size, size)
    })
  }
}

const config = {
  plugins: props.datalabels ? [segmentImage, ChartDataLabels] : [segmentImage],
}

const data = {
  datasets: [props.dataset],
  config: config,
  options: {
    cutout: props.cutout,
    responsive: true,
    plugins: {
      datalabels: {
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 15,
        },
      }
    }
  },
}
</script>

<style lang="scss" scoped>
.chart-doughnut {
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;

  .container-icon-grand-charles {
    align-content: center;
    text-align: center;
    position: absolute;
    width: 180px;
    height: 90px;
    top: calc(50% - 60px);
    left: calc(50% - 85px);

    p {
      margin-bottom: 10px;
      color: var(--vc-primary);
      font-weight: 700;
      font-size: 26px;
    }
  }

  .info {
    display: flex;
    flex-direction: column;
    position: absolute;
    max-width: 50%;

    .label,
    .data {
      color: var(--vc-primary);
      font-size: 15px;

      &.small {
        font-size: 15px;
      }
    }

    .label {
      text-transform: uppercase;
    }

    .data {
      font-weight: 700;
      font-size: 28px;
      line-height: 1;

      &.small {
        font-size: 28px;
      }
    }

    &.top {
      top: 0;
    }

    &.bottom {
      bottom: 0;
    }

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
      text-align: end;
    }
  }
}
</style>
